import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Stack,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Notes as NotesIcon,
  History as HistoryIcon
} from '@mui/icons-material';

const FormLogHistory = ({ form, individual }) => {
  const [expanded, setExpanded] = useState(false);
  
  const formatDate = (dateString, compact = false) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    if (compact) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: '2-digit'
      });
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted': return 'success';
      case 'pending': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'submitted': return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'pending': return <ScheduleIcon sx={{ fontSize: 16 }} />;
      case 'expired': return <ScheduleIcon color="error" sx={{ fontSize: 16 }} />;
      default: return <AssignmentIcon sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <Card sx={{ mb: 1.5, boxShadow: 1 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Compact Header Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 500 }}>
              {form.report_name}
            </Typography>
            <Chip 
              icon={getStatusIcon(form.status)}
              label={form.status?.toUpperCase() || 'UNKNOWN'}
              color={getStatusColor(form.status)}
              size="small"
              sx={{ height: 24 }}
            />
          </Box>
          <IconButton 
            size="small" 
            onClick={() => setExpanded(!expanded)}
            sx={{ ml: 1 }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        
        {/* Compact Info Row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* Individual */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
            <Avatar sx={{ bgcolor: 'primary.light', width: 28, height: 28 }}>
              <PersonIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {individual?.name || 'Unknown'}
            </Typography>
          </Box>
          
          {/* Key Dates */}
          <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
            <Tooltip title="Document Date">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Doc
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDate(form.due_date, true)}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
            
            {form.collected_date && (
              <Tooltip title="Collection Date">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Collected
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(form.collected_date, true)}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            )}
          </Stack>
          
          {/* Quick Info Icons */}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            {form.notes && (
              <Tooltip title="Has notes">
                <NotesIcon sx={{ fontSize: 18, color: 'info.main' }} />
              </Tooltip>
            )}
            {(form.created_by_user || form.submitted_by_user || form.collected_by_user) && (
              <Tooltip title="Has activity log">
                <HistoryIcon sx={{ fontSize: 18, color: 'action.active' }} />
              </Tooltip>
            )}
          </Box>
        </Box>
        
        {/* Expandable Details */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 1.5 }} />
          
          {/* Staff Activity */}
          {(form.created_by_user || form.submitted_by_user || form.collected_by_user) && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                STAFF ACTIVITY LOG
              </Typography>
              <Stack spacing={0.5}>
                {form.created_by_user && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon sx={{ fontSize: 16, color: 'info.main' }} />
                    <Typography variant="caption">
                      Created by <strong>{form.created_by_user}</strong>
                    </Typography>
                  </Box>
                )}
                {form.submitted_by_user && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="caption">
                      Submitted by <strong>{form.submitted_by_user}</strong>
                    </Typography>
                  </Box>
                )}
                {form.collected_by_user && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                    <Typography variant="caption">
                      Collected by <strong>{form.collected_by_user}</strong>
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
          
          {/* Notes */}
          {form.notes && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                NOTES
              </Typography>
              <Typography variant="body2" sx={{ 
                bgcolor: 'grey.50', 
                p: 1, 
                borderRadius: 0.5,
                fontSize: '0.875rem'
              }}>
                {form.notes}
              </Typography>
            </Box>
          )}
          
          {/* Additional Dates */}
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {form.submission_date && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Submission Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(form.submission_date)}
                  </Typography>
                </Box>
              )}
              {individual?.intake_date && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Intake Date
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(individual.intake_date)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default FormLogHistory;